import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Wallet, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Compact Net-Worth summary card + trend sparkline. Meant to sit at the top
 * of the Investments page. Shows assets, liabilities, net worth, and a
 * 26-week trend.
 */
export default function NetWorthCard() {
    const [snap, setSnap] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, h] = await Promise.all([
                api.get("/net-worth"),
                api.get("/net-worth/history?weeks=26"),
            ]);
            setSnap(s.data);
            setHistory((h.data.series || []).map((r) => ({
                date: r.as_of_date, net_worth: r.net_worth,
                assets: r.total_assets, liabilities: r.total_liabilities,
            })));
        } catch {
            setSnap(null);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="rounded-lg border border-stone-200 bg-white p-6 text-sm text-stone-500">Computing net worth…</div>;
    if (!snap) return null;

    const positive = snap.net_worth >= 0;
    const trend = snap.trend;

    return (
        <div className="rounded-xl border border-stone-200 bg-white p-6 mb-6" data-testid="net-worth-card">
            <div className="flex flex-wrap items-start justify-between gap-6">
                {/* Big net worth */}
                <div>
                    <div className="eyebrow flex items-center gap-1.5">
                        <Wallet size={11} /> Net Worth · Vault roll-up
                    </div>
                    <div className={`font-display text-5xl mt-2 tabular-nums ${positive ? "text-stone-900" : "text-red-800"}`} data-testid="nw-total">
                        {fmtMoney(snap.net_worth)}
                    </div>
                    {trend && (
                        <div className={`inline-flex items-center gap-1 mt-2 text-[12px] ${trend.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {trend.delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {trend.delta >= 0 ? "+" : ""}{fmtMoney(trend.delta)} ({trend.delta_pct >= 0 ? "+" : ""}{trend.delta_pct}%) vs last week
                        </div>
                    )}
                </div>

                {/* Breakdown grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 min-w-[300px]">
                    <div>
                        <div className="text-[11px] text-stone-500 uppercase tracking-widest">Assets</div>
                        <div className="font-mono-figure text-lg mt-0.5 text-emerald-700">{fmtMoney(snap.total_assets)}</div>
                    </div>
                    <div>
                        <div className="text-[11px] text-stone-500 uppercase tracking-widest">Liabilities</div>
                        <div className="font-mono-figure text-lg mt-0.5 text-red-700">{fmtMoney(snap.total_liabilities)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Investments</div>
                        <div className="font-mono-figure text-sm">{fmtMoney(snap.assets.investments_current)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Card outstanding</div>
                        <div className="font-mono-figure text-sm">{fmtMoney(snap.liabilities.credit_cards)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Bank balances</div>
                        <div className="font-mono-figure text-sm">{fmtMoney(snap.assets.bank_balances)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Bills due</div>
                        <div className="font-mono-figure text-sm">{fmtMoney(snap.liabilities.recurring_bills)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Registered assets</div>
                        <div className="font-mono-figure text-sm">{fmtMoney(snap.assets.assets_registry)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] text-stone-500">Unrealised gain</div>
                        <div className={`font-mono-figure text-sm ${snap.assets.investments_gain >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {snap.assets.investments_gain >= 0 ? "+" : ""}{fmtMoney(snap.assets.investments_gain)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Trend chart */}
            {history.length > 1 && (
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <div className="eyebrow mb-2">26-week trend</div>
                    <div style={{ width: "100%", height: 120 }}>
                        <ResponsiveContainer>
                            <LineChart data={history} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                                <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} fontSize={9} tick={{ fill: "#78716c" }} />
                                <YAxis hide />
                                <Tooltip
                                    contentStyle={{ fontSize: 11, padding: 6 }}
                                    formatter={(v) => [fmtMoney(v), "Net worth"]}
                                    labelFormatter={(l) => l}
                                />
                                <Line type="monotone" dataKey="net_worth" stroke="#0f172a" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
            <div className="mt-3 text-right">
                <Link to="/cash-flow" className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-900">
                    See cash flow forecast <ChevronRight size={11} />
                </Link>
            </div>
        </div>
    );
}
